import uPlot, { type Options, type AlignedData } from "uplot";
import "uplot/dist/uPlot.min.css";
import { fmtEur } from "./data";

// Read live token values so the chart restyles on theme switch.
function readToken(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

function fmtMonthDay(ts: number): string {
  const d = new Date(ts * 1000);
  return d
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
    .toUpperCase();
}

function fmtFullDate(ts: number): string {
  const d = new Date(ts * 1000);
  return d
    .toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .toUpperCase();
}

interface SeriesDef {
  key: string;
  label: string;
  color: () => string;
  dash?: number[];
  width?: number;
  fill?: boolean;
}

export interface BalanceChartOptions {
  host: HTMLElement;
  data: AlignedData; // [timestamps, payIn, payOut, operational]
  series: SeriesDef[];
  netEl: HTMLElement;
}

export interface BalanceChart {
  destroy(): void;
  setRange(days: number): void;
  setVisible(key: string, visible: boolean): void;
}

/**
 * Build a uPlot chart styled to the Ledger system: hairline grid,
 * currentColor strokes, custom hover readout, click-on-legend toggling.
 *
 * Range changes use uPlot's `setScale` so we keep one chart instance
 * rather than re-mounting.
 */
export function buildBalanceChart(opts: BalanceChartOptions): BalanceChart {
  const visible: Record<string, boolean> = Object.fromEntries(
    opts.series.map((s) => [s.key, true]),
  );

  const readout = document.createElement("div");
  readout.className = "chart-readout";
  opts.host.appendChild(readout);

  let chart: uPlot | null = null;
  const data = opts.data;

  function buildOpts(): Options {
    const ink = readToken("--ink-primary");
    const muted = readToken("--ink-muted");
    const lineCol = readToken("--line-default");
    const lineSub = readToken("--line-subtle");

    const series: Options["series"] = [
      { label: "Date" },
      ...opts.series.map((s) => {
        const color = s.color();
        return {
          label: s.label,
          stroke: color,
          width: s.width ?? 1.6,
          dash: s.dash,
          show: visible[s.key],
          fill: s.fill
            ? `color-mix(in srgb, ${color} 14%, transparent)`
            : undefined,
          points: { show: false },
          spanGaps: true,
        };
      }),
    ];

    return {
      width: opts.host.clientWidth,
      height: opts.host.clientHeight,
      legend: { show: false },
      cursor: {
        drag: { setScale: false, x: false, y: false },
        x: true,
        y: false,
        points: { show: false },
        focus: { prox: 30 },
      },
      hooks: {
        ready: [
          (u) => {
            const c = u.over.querySelector(
              ".u-cursor-x",
            ) as HTMLDivElement | null;
            if (c) {
              c.style.background = ink;
              c.style.opacity = "0.3";
              c.style.width = "1px";
            }
          },
        ],
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null) {
              readout.style.display = "none";
              return;
            }
            const left = u.cursor.left ?? 0;
            const ts = u.data[0]?.[idx];
            if (ts == null) return;

            const rows = opts.series
              .map((s, i) => {
                if (!visible[s.key]) return "";
                const v = u.data[i + 1]?.[idx];
                if (v == null) return "";
                const dashStyle = s.dash
                  ? `background-image:linear-gradient(90deg, ${s.color()} 50%, transparent 50%);background-size:4px 2px;background-color:transparent;`
                  : `background:${s.color()};`;
                return `<div class="chart-readout-row">
                  <span class="chart-readout-label">
                    <span class="chart-readout-mark" style="${dashStyle}"></span>
                    ${s.label}
                  </span>
                  <span class="chart-readout-value">${fmtEur(v as number, { compact: true })}</span>
                </div>`;
              })
              .join("");

            readout.innerHTML = `<div class="chart-readout-date">${fmtFullDate(ts as number)}</div>${rows}`;
            readout.style.display = "block";

            const hostRect = opts.host.getBoundingClientRect();
            const r = readout.getBoundingClientRect();
            let x = left + 12;
            if (x + r.width > hostRect.width - 8) x = left - r.width - 12;
            readout.style.left = `${x}px`;
            readout.style.top = `8px`;
          },
        ],
      },
      scales: {
        x: { time: true },
        y: {
          // Hug the data, but never crop the line. Pad 8% above/below;
          // floor at 0 for flow charts so the baseline reads honestly.
          range: (_self, dataMin, dataMax) => {
            if (dataMin == null || dataMax == null) return [0, 1];
            const pad = (dataMax - dataMin) * 0.08;
            return [Math.max(0, dataMin - pad), dataMax + pad];
          },
        },
      },
      axes: [
        {
          stroke: muted,
          grid: { show: false },
          ticks: { show: true, stroke: lineCol, width: 1, size: 4 },
          font: '600 10px "Manrope", sans-serif',
          labelFont: '600 10px "Manrope", sans-serif',
          values: (_u, splits) => splits.map((v) => fmtMonthDay(v)),
          space: 70,
          size: 32,
        },
        {
          stroke: muted,
          grid: { show: true, stroke: lineSub, width: 1, dash: [2, 4] },
          ticks: { show: false },
          font: '600 10px "Manrope", sans-serif',
          labelFont: '600 10px "Manrope", sans-serif',
          values: (_u, splits) =>
            splits.map((v) =>
              v >= 1_000_000
                ? `€${(v / 1_000_000).toFixed(1)}M`
                : `€${(v / 1000).toFixed(0)}k`,
            ),
          size: 60,
        },
      ],
      series,
    };
  }

  function rebuild() {
    if (chart) chart.destroy();
    chart = new uPlot(buildOpts(), data, opts.host);
  }

  function applyRange(days: number) {
    if (!chart) return;
    const ts = data[0] as number[];
    if (!ts || ts.length === 0) return;
    const lastTs = ts[ts.length - 1];
    if (lastTs == null) return;
    const fromTs = lastTs - days * 86400;
    chart.setScale("x", { min: fromTs, max: lastTs });

    // Recompute net for visible window
    const fromIdx = ts.findIndex((t) => t >= fromTs);
    const inArr = (data[1] ?? []).slice(fromIdx) as number[];
    const outArr = (data[2] ?? []).slice(fromIdx) as number[];
    const totalIn = inArr.reduce((a, b) => a + b, 0);
    const totalOut = outArr.reduce((a, b) => a + b, 0);
    const net = totalIn - totalOut;
    opts.netEl.innerHTML = `In <strong>${fmtEur(totalIn, { compact: true })}</strong> · Out <strong>${fmtEur(totalOut, { compact: true })}</strong> · Net <strong class="${net < 0 ? "is-down" : ""}">${net >= 0 ? "+" : ""}${fmtEur(net, { compact: true })}</strong>`;
  }

  rebuild();
  applyRange(30);

  const ro = new ResizeObserver(() => {
    if (chart)
      chart.setSize({
        width: opts.host.clientWidth,
        height: opts.host.clientHeight,
      });
  });
  ro.observe(opts.host);

  const themeObs = new MutationObserver(() => rebuild());
  themeObs.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  return {
    destroy() {
      ro.disconnect();
      themeObs.disconnect();
      if (chart) chart.destroy();
      readout.remove();
    },
    setRange(days) {
      applyRange(days);
    },
    setVisible(key, vis) {
      visible[key] = vis;
      rebuild();
    },
  };
}
